import { Link } from "react-router-dom";
import { Logo } from "@components/ui/Logo";
import paymentMethodsImage from "@assets/images/Frame-1300@2x.png";
import submitArrowIcon from "@assets/images/Frame-93.svg";

/**
 * Main site footer component
 *
 * Shared across all pages. Update based on Figma design.
 */

interface FooterColumnProps {
  title: string;
  links: Array<{ label: string; href: string }>;
}

function FooterColumn({ title, links }: FooterColumnProps) {
  return (
    <div className="w-full sm:w-[180px] flex flex-col items-start gap-6 lg:gap-[29px]">
      <h3 className="self-stretch text-[18px] lg:text-[20px] tracking-[-0.04em] font-semibold text-[#fffcfb] font-albert-sans">
        {title}
      </h3>
      <ul className="self-stretch flex flex-col items-start gap-5 lg:gap-[31px]">
        {links.map((link) => (
          <li key={link.href} className="self-stretch">
            <Link
              to={link.href}
              className="self-stretch text-[16px] lg:text-[18px] tracking-[-0.04em] text-[#fffcfb] font-albert-sans hover:text-white transition-colors"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

const customerLinks = [
  { label: "Pay Bill", href: "/services/pay-bill" },
  { label: "New Connection", href: "/services/new-connections" },
  { label: "FAQs", href: "/faq" },
  { label: "Contact Us", href: "/contact" },
];

const companyLinks = [
  { label: "About", href: "/about" },
  { label: "Careers", href: "/careers" },
  { label: "Business Solutions", href: "/business" },
  { label: "Sustainability", href: "/sustainability" },
  { label: "News & Updates", href: "/news" },
];

const legalLinks = [
  { label: "Privacy & Cookies Policy", href: "/privacy-policy" },
];

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full bg-[#1e1f24] overflow-hidden py-16 lg:py-32 px-4 md:px-8 lg:px-[120px]">
      <div className="w-full max-w-[1488px] mx-auto flex flex-col gap-16 lg:gap-[100px]">
        {/* Top Section */}
        <div className="self-stretch flex flex-col lg:flex-row items-start lg:items-end justify-between gap-12 lg:gap-5">
          {/* Left Column */}
          <div className="w-full lg:w-[558px] flex flex-col items-start gap-10 lg:gap-16">
            {/* Logo and Address */}
            <div className="self-stretch flex flex-col items-start gap-6 lg:gap-8">
              <Logo variant="white" imgClassName="h-[72px] md:h-[88px]" />
              <p className="self-stretch text-[16px] lg:text-[18px] tracking-[-0.04em] text-[#b9bbc6] font-albert-sans">
                P.O. Box 12345, Dubai Investment Park 1, Dubai, United Arab
                Emirates
                <br />
                Trade License No.: 1234567 – Issued by Dubai Economic Department
                <br />
                Certified by Dubai Civil Defense and Emirates Authority for
                Standardization and Metrology (ESMA)
              </p>
            </div>

            {/* Newsletter */}
            <div className="w-full lg:w-[497px] flex flex-col items-start gap-4 lg:gap-[18px]">
              <h2 className="text-[22px] lg:text-[28px] tracking-[-0.04em] font-normal text-[#b9bbc6] font-albert-sans">
                Stay up to date with GIS
              </h2>
              <form className="self-stretch flex items-center py-0 pl-0 pr-2 gap-4">
                <div className="h-[65px] lg:h-[79px] flex-1 flex flex-col items-end justify-center py-[7px] px-2 relative min-w-[264px]">
                  <div className="w-full h-full absolute top-0 left-0 rounded-[40px] bg-[#f9f9fb] border border-[#8b8d98] flex items-center py-4 px-6">
                    <input
                      type="email"
                      placeholder="Enter Email Address"
                      className="w-full border-none outline-none font-light font-albert-sans text-[16px] lg:text-[18px] bg-transparent italic text-[#62636c] p-0 focus:outline-none"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="shrink-0"
                  aria-label="Subscribe"
                >
                  <img
                    src={submitArrowIcon}
                    alt=""
                    className="w-[55px] lg:w-[67.2px] rounded-[168px] hover:opacity-80 transition-opacity"
                  />
                </button>
              </form>
            </div>
          </div>

          {/* Navigation Columns */}
          <div className="flex flex-col sm:flex-row items-center gap-10 lg:gap-20">
            <FooterColumn title="Customers" links={customerLinks} />
            <FooterColumn title="Company" links={companyLinks} />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="self-stretch flex flex-col lg:flex-row items-start lg:items-end gap-8 lg:gap-[436px]">
          {/* Payment Methods and Copyright */}
          <div className="w-full lg:w-[446px] flex flex-col items-start gap-6 lg:gap-8">
            <img
              src={paymentMethodsImage}
              alt="Accepted payment methods"
              className="w-[431px] h-[24.4px] object-contain"
              loading="lazy"
            />
            <p className="self-stretch text-[16px] lg:text-[18px] tracking-[-0.04em] text-[#b9bbc6] font-albert-sans">
              © {currentYear} Gaz Investment in Oil and Gas Supply L.L.C. All rights
              reserved.
            </p>
          </div>

          {/* Legal Links */}
          <div className="flex flex-wrap lg:flex-nowrap items-start gap-6 lg:gap-10">
            {legalLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-[16px] lg:text-[18px] tracking-[-0.04em] text-[#b9bbc6] font-albert-sans hover:text-white transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
